import toast from 'react-hot-toast'
import { useConstructShip, type ResourcesDto, type ShipBlueprintDto, type ShipyardDto } from '@/api/hooks'
import { ShipCombatStatsRow } from '@/components/ShipCombatStats'
import { ShipIcon, GoldIcon, WoodIcon, RumIcon, AnchorIcon } from '@/components/game/GameIcons'
import { GoldButton } from '@/components/game/ui'

interface Props {
  shipyard: ShipyardDto
  resources: ResourcesDto
  berths?: { used: number; total: number } | null
}

export function Shipyard({ shipyard, resources, berths }: Props) {
  const queueFull = shipyard.slotsUsed >= shipyard.slots
  const berthsFull = !!berths && berths.used >= berths.total

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-3">
        <h2 className="font-display text-2xl text-sand-100 flex items-center gap-2.5">
          <AnchorIcon className="w-6 h-6 text-sand-300" /> Chantier naval
          <span className="font-body text-sm text-ocean-300">· niveau {shipyard.level}</span>
        </h2>
        <ShipyardSlots used={shipyard.slotsUsed} total={shipyard.slots} />
      </div>
      {queueFull && (
        <p className="text-xs text-ocean-400 italic mb-3 flex items-start gap-1.5">
          <span aria-hidden="true">⚓</span>
          <span>
            Tous les emplacements sont occupés. Améliore le <strong className="text-ocean-200">Chantier naval</strong>{' '}
            (page « Mon île ») pour en débloquer, ou attends la fin d'une construction.
          </span>
        </p>
      )}
      {berthsFull && berths && (
        <p className="text-xs text-ocean-400 italic mb-3 flex items-start gap-1.5">
          <span aria-hidden="true">⚓</span>
          <span>
            Tous tes mouillages sont occupés ({berths.used}/{berths.total}) : améliore la{' '}
            <strong className="text-ocean-200">Capitainerie</strong> (page « Mon île ») pour agrandir ta flotte.
          </span>
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {shipyard.buildable.map((bp) => (
          <ShipBlueprintCard key={bp.type} blueprint={bp} resources={resources} queueFull={queueFull} berthsFull={berthsFull} />
        ))}
      </div>
    </section>
  )
}

/**
 * Build-slot gauge: one pip per slot (filled = occupied), plus an at-a-glance
 * "N libre(s)" / "File pleine" readout. Slots are unlocked by upgrading the
 * shipyard, so this also tells the captain whether they can start a new build.
 */
function ShipyardSlots({ used, total }: { used: number; total: number }) {
  const free = Math.max(0, total - used)
  return (
    <span className="inline-flex items-center gap-2 text-sm" title={`${used}/${total} emplacement(s) occupé(s)`}>
      <span className="inline-flex items-center gap-1">
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={`w-3.5 h-3.5 rounded-sm border ${i < used ? 'bg-sand-300/70 border-sand-300' : 'border-sand-500/40 bg-ocean-900/40'}`}
          />
        ))}
      </span>
      <span className={`font-medium ${free > 0 ? 'text-green-300' : 'text-red-400'}`}>
        {free > 0 ? `${free} emplacement${free > 1 ? 's' : ''} libre${free > 1 ? 's' : ''}` : 'File pleine'}
      </span>
    </span>
  )
}

function ShipBlueprintCard({
  blueprint,
  resources,
  queueFull,
  berthsFull,
}: {
  blueprint: ShipBlueprintDto
  resources: ResourcesDto
  queueFull: boolean
  berthsFull: boolean
}) {
  const construct = useConstructShip()
  const mins = Math.max(1, Math.round(blueprint.timeSeconds / 60))
  const canAfford =
    resources.gold >= blueprint.cost.gold &&
    resources.wood >= blueprint.cost.wood &&
    resources.rum >= blueprint.cost.rhum

  const gate = !blueprint.unlocked
    ? { ok: false, label: `Chantier niv. ${blueprint.requiredShipyardLevel}`, reason: `Chantier naval niveau ${blueprint.requiredShipyardLevel} requis` }
    : queueFull
      ? { ok: false, label: 'File occupée', reason: 'Tous les emplacements du chantier sont occupés' }
      : berthsFull
        ? { ok: false, label: 'Mouillages complets', reason: 'Tous tes mouillages sont occupés. Améliore la Capitainerie.' }
        : !canAfford
          ? { ok: false, label: 'Ressources insuffisantes', reason: 'Ressources insuffisantes' }
          : { ok: true, label: 'Construire', reason: 'Lancer la construction' }

  return (
    <div className={`rounded-2xl p-5 border flex flex-col gap-3 ${blueprint.unlocked ? 'border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40' : 'border-sand-500/12 bg-gradient-to-b from-ocean-700/15 to-[#06222b]/25 opacity-70'}`}>
      <div className="flex items-start justify-between">
        <h3 className="font-display text-lg text-sand-300 flex items-center gap-2">
          <ShipIcon type={blueprint.type} className="w-5 h-5 text-sand-300" /> {blueprint.typeLabel}
        </h3>
        <span className="text-xs text-ocean-300">
          {blueprint.unlocked ? `⏱ ${mins} min` : `Chantier niv. ${blueprint.requiredShipyardLevel}`}
        </span>
      </div>

      <ShipCombatStatsRow stats={blueprint.combatStats} />

      <div className="mt-auto pt-2 border-t border-sand-500/12">
        <p className="text-xs text-ocean-300 mb-2 flex items-center gap-1.5 flex-wrap">
          Coût :
          <Cost have={resources.gold} need={blueprint.cost.gold} Icon={GoldIcon} />
          <Cost have={resources.wood} need={blueprint.cost.wood} Icon={WoodIcon} />
          <Cost have={resources.rum} need={blueprint.cost.rhum} Icon={RumIcon} />
        </p>
        <GoldButton
          onClick={() =>
            construct.mutate(blueprint.type, {
              onSuccess: () => toast.success(`${blueprint.typeLabel} : construction lancée !`),
              onError: (err) => toast.error(err.message || 'Construction impossible'),
            })
          }
          disabled={!gate.ok || construct.isPending}
          title={gate.reason}
          className="w-full text-sm px-3 py-1.5"
        >
          {construct.isPending ? 'Lancement…' : gate.label}
        </GoldButton>
      </div>
    </div>
  )
}

function Cost({ have, need, Icon }: { have: number; need: number; Icon: (p: { className?: string }) => React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 ${have >= need ? 'text-sand-300' : 'text-red-400'}`}>
      <Icon className="w-3.5 h-3.5" /> {need}
    </span>
  )
}
